import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { chunk } from 'lodash-es';

import { useCatalogDepartments, UseCatalogDepartmentsReturn } from '../../hooks/useCatalogDepartments/useCatalogDepartments';
import { useCatalogContext } from '../../state/Catalog';
import { useCatalogRouting } from '../../useCatalogRouting';
import { useHistory, useLocation } from '@react-router';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { MinimalCatalogCategory } from '../../Catalog.types';
import { withWindow } from '@shared/utils/withWindow';
import { useMegaNavHealth } from '@apps/registry/common/components/Catalog/components/MegaNav/MegaNav.health';
import { clearLocationState } from '../../hooks/useTrackCatalogPageViaLocationState';
import { useCatalogLoggingContext } from '../../Catalog.logging';
import { useProductCatalogAdsQuery } from '@graphql/generated';
import { BrandPromo } from '@apps/registry/admin/components/BrandCard/BrandCard';

type Department = UseCatalogDepartmentsReturn['departments'][number];
export type DepartmentPartitions = Array<Array<Department['children'][number] | null>>;

const OPEN_TRANSITION_DELAY = 390;

export const useMegaNav = () => {
  const { selectedCategory } = useCatalogContext();
  const { getToCategoryPath, getToCurationDetailsPath, getToRootCurationsPath } = useCatalogRouting();
  const { departments, error: catalogDepartmentsError, loading: loadingDepartments } = useCatalogDepartments();
  const { trackCategoryNavigateAction } = useCatalogLoggingContext();

  const [activeDepartmentIndex, setActiveDepartmentIndex] = useState<number>(-1);
  const dropdownRefs = useRef<Array<HTMLUListElement>>([]);
  const departmentNavTimeoutRef = useRef<number | undefined>();

  const history = useHistory();
  const loc = useLocation();
  const { data } = useProductCatalogAdsQuery({
    batchMode: 'fast',
  });

  // Reset active department on history change
  useEffect(() => {
    const unregister = history.listen((_loc, _action) => {
      setActiveDepartmentIndex(-1);
    });

    return () => {
      unregister();
    };
  }, [history]);

  const { onLoadComplete, onLoadFailure } = useMegaNavHealth();
  useEffect(() => {
    if (catalogDepartmentsError) {
      onLoadFailure('ProductCatalogDepartmentsQuery failed', catalogDepartmentsError);
    } else if (departments) {
      onLoadComplete();
    }
  }, [catalogDepartmentsError, onLoadFailure, departments, onLoadComplete]);

  const partitionedDepartments = useMemo(() => {
    const partitionedDepts: Array<[department: (typeof departments)[number], partitions: DepartmentPartitions, promo: BrandPromo]> = [];

    departments.forEach(dep => {
      const brandPromo = data?.productCatalogAds?.departmentBrandPromos?.find(depBrandPromo => depBrandPromo?.department?.id === dep?.id)?.navBar;
      const showBrandPromo = !!brandPromo;
      const columnCount = showBrandPromo ? 3 : 4;
      const partitions: DepartmentPartitions = showBrandPromo ? [[], [], []] : [[], [], [], []];
      const categories = dep.children || [];

      const chunks = chunk(categories, columnCount) as Array<Array<(typeof categories)[number] | null>>;

      const lastChunk = chunks[chunks.length - 1];

      if (lastChunk?.length) {
        const spotsToFill = columnCount - lastChunk.length;
        if (chunks.length > 1 && spotsToFill < columnCount) {
          for (let i = 0; i < spotsToFill; i++) {
            lastChunk.unshift(null);
          }
        }
      }

      chunks.forEach(chunk => {
        chunk.forEach((item, partitionIndex) => {
          partitions[partitionIndex].push(item);
        });
      });

      partitionedDepts.push([dep, partitions, brandPromo as BrandPromo]);
    });

    return partitionedDepts;
  }, [data?.productCatalogAds?.departmentBrandPromos, departments]);

  const collectMegaMenuRef = useCallback((e: HTMLUListElement | null, deptIndex: number) => {
    dropdownRefs.current[deptIndex] = e || dropdownRefs.current[deptIndex];
  }, []);

  const getDepartmentRoute = useCallback(
    (department: Department) => {
      const isCurations = !!department.__IS_CURATIONS__;
      return isCurations ? getToRootCurationsPath() : getToCategoryPath(department.publicUrlPath);
    },
    [getToRootCurationsPath, getToCategoryPath],
  );

  const getIsDeptActive = useCallback(
    (dep: Department) => {
      return activeDepartmentIndex === -1 && loc.pathname.endsWith(getDepartmentRoute(dep));
    },
    [loc, activeDepartmentIndex, getDepartmentRoute],
  );

  const getIsNavItemActive = useCallback(
    (match: Maybe<object>, department: (typeof departments)[number]) => {
      if (match) {
        return true;
      }
      if (selectedCategory) {
        return selectedCategory.lineageCsp[1]?.id === department.id || selectedCategory.id === department.id;
      }
      return false;
    },
    [selectedCategory],
  );

  const resetDepNavTracker = useEventCallback(() => {
    // Clear all timeouts to avoid unwanted reopening of menu
    withWindow(global => {
      global.clearTimeout(departmentNavTimeoutRef.current);
    });
    setActiveDepartmentIndex(-1);
  });

  const handleOnCategoryClick = useCallback(
    (category: MinimalCatalogCategory) => {
      trackCategoryNavigateAction({
        category,
        navigationSource: 'meganav',
      });
      clearLocationState();
      resetDepNavTracker();
    },
    [trackCategoryNavigateAction, resetDepNavTracker],
  );

  const debouncedSetActiveDepartmentIndex = useEventCallback((nextIndex: number) => {
    withWindow(global => {
      // Two states:
      //  - meganav menu is not open: add delay to initial open to prevent early trigger
      //  - meganav menu is open: add delay to switching tabs to prevent jitter
      global.clearTimeout(departmentNavTimeoutRef.current);

      departmentNavTimeoutRef.current = global.setTimeout(() => {
        setActiveDepartmentIndex(nextIndex);
      }, OPEN_TRANSITION_DELAY);
    });
  });

  const handleOnMouseOver = useEventCallback((_id: string, depIndex: number) => {
    debouncedSetActiveDepartmentIndex(depIndex);
  });

  return {
    loadingDepartments,
    activeDepartmentIndex,
    handleOnCategoryClick,
    handleOnMouseLeave: resetDepNavTracker,
    collectMegaMenuRef,
    getDepartmentRoute,
    getIsDeptActive,
    getIsNavItemActive,
    getToCategoryPath,
    getToCurationDetailsPath,
    navHeight: (dropdownRefs.current[activeDepartmentIndex]?.scrollHeight ?? -2) + 2,
    partitionedDepartments,
    handleOnMouseOver,
  };
};
